import { useEffect, useRef } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import PublicIcon from "@mui/icons-material/Public";
import AT from "country-flag-icons/react/3x2/AT";
import AU from "country-flag-icons/react/3x2/AU";
import BE from "country-flag-icons/react/3x2/BE";
import CA from "country-flag-icons/react/3x2/CA";
import CH from "country-flag-icons/react/3x2/CH";
import CN from "country-flag-icons/react/3x2/CN";
import CY from "country-flag-icons/react/3x2/CY";
import DE from "country-flag-icons/react/3x2/DE";
import EC from "country-flag-icons/react/3x2/EC";
import EE from "country-flag-icons/react/3x2/EE";
import ES from "country-flag-icons/react/3x2/ES";
import EU from "country-flag-icons/react/3x2/EU";
import FI from "country-flag-icons/react/3x2/FI";
import FR from "country-flag-icons/react/3x2/FR";
import GB from "country-flag-icons/react/3x2/GB";
import GR from "country-flag-icons/react/3x2/GR";
import IE from "country-flag-icons/react/3x2/IE";
import IT from "country-flag-icons/react/3x2/IT";
import JP from "country-flag-icons/react/3x2/JP";
import LT from "country-flag-icons/react/3x2/LT";
import LU from "country-flag-icons/react/3x2/LU";
import LV from "country-flag-icons/react/3x2/LV";
import ME from "country-flag-icons/react/3x2/ME";
import MT from "country-flag-icons/react/3x2/MT";
import NL from "country-flag-icons/react/3x2/NL";
import NZ from "country-flag-icons/react/3x2/NZ";
import PT from "country-flag-icons/react/3x2/PT";
import SI from "country-flag-icons/react/3x2/SI";
import SK from "country-flag-icons/react/3x2/SK";
import SV from "country-flag-icons/react/3x2/SV";
import US from "country-flag-icons/react/3x2/US";
import XK from "country-flag-icons/react/3x2/XK";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import useCurrentTime from "hooks/useCurrentTime";
import useEconomicCalendarEventNotifications from "hooks/useEconomicCalendarEventNotifications";
import { getCountryCode, isCalendarEventPast } from "lib/economicCalendar";

export const IMPACT_STYLES = {
  "-1": { color: "#667085", background: "#EEF1F5", dot: "#98A2B3" },
  0: { color: "#176B63", background: "#E4F4F0", dot: "#2A9D8F" },
  1: { color: "#8A5B00", background: "#FFF2CC", dot: "#E9A900" },
  2: { color: "#A92C2C", background: "#FDE8E7", dot: "#D84A43" },
};

const COUNTRY_FLAG_COMPONENTS = {
  AT,
  AU,
  BE,
  CA,
  CH,
  CN,
  CY,
  DE,
  EC,
  EE,
  ES,
  EU,
  FI,
  FR,
  GB,
  GR,
  IE,
  IT,
  JP,
  LT,
  LU,
  LV,
  ME,
  MT,
  NL,
  NZ,
  PT,
  SI,
  SK,
  SV,
  US,
  XK,
};

const formatTime = (value, language) => {
  const localTime = String(value || "").match(/T(\d{2}):(\d{2})/);
  if (localTime) return `${localTime[1]}:${localTime[2]}`;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--:--";

  return new Intl.DateTimeFormat(language === "es" ? "es-ES" : "en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const CountryFlag = ({ country, currency, isPast, compact = false }) => {
  const countryCode = getCountryCode(country, currency);
  const Flag = countryCode ? COUNTRY_FLAG_COMPONENTS[countryCode] : null;

  return (
    <Box
      sx={{
        width: compact ? 24 : 32,
        height: compact ? 18 : 24,
        borderRadius: compact ? "4px" : "5px",
        overflow: "hidden",
        flexShrink: 0,
        display: "grid",
        placeItems: "center",
        color: "#718096",
        background: "#E8EDF3",
        boxShadow: "0 1px 3px rgba(37, 62, 102, 0.18)",
        filter: isPast ? "grayscale(0.75)" : "none",
        "& svg": { width: "100%", height: "100%", display: "block" },
      }}
    >
      {Flag ? (
        <Flag title={country || currency} />
      ) : (
        <PublicIcon fontSize="small" />
      )}
    </Box>
  );
};

CountryFlag.propTypes = {
  country: PropTypes.string,
  currency: PropTypes.string,
  isPast: PropTypes.bool.isRequired,
  compact: PropTypes.bool,
};

const EconomicCalendarEventList = ({
  items = [],
  loading = false,
  compact = false,
  autoScrollToUpcoming = false,
  autoScrollKey = "",
  notificationDate = "",
}) => {
  const { t, i18n } = useTranslation("economicCalendar");
  const now = useCurrentTime();
  const eventNotifications = useEconomicCalendarEventNotifications(
    notificationDate
  );
  const upcomingEventRef = useRef(null);
  const lastScrollKeyRef = useRef("");
  const firstUpcomingIndex = items.findIndex(
    (item) => !isCalendarEventPast(item.datetime, now)
  );
  const itemSignature = items
    .map((item) => `${item.datetime}-${item.currency}-${item.name}`)
    .join("|");
  const currentScrollKey = `${autoScrollKey}|${itemSignature}`;

  useEffect(() => {
    if (
      !autoScrollToUpcoming ||
      loading ||
      firstUpcomingIndex < 0 ||
      lastScrollKeyRef.current === currentScrollKey
    ) {
      return undefined;
    }

    lastScrollKeyRef.current = currentScrollKey;
    const frame = window.requestAnimationFrame(() => {
      const target = upcomingEventRef.current;
      let scrollContainer = target?.parentElement;
      while (scrollContainer) {
        const isCalendarScrollContainer =
          scrollContainer.dataset?.calendarScrollContainer === "true";
        const hasScrollableContent =
          scrollContainer.scrollHeight > scrollContainer.clientHeight + 1;
        if (isCalendarScrollContainer && hasScrollableContent) break;
        scrollContainer = scrollContainer.parentElement;
      }
      if (!target || !scrollContainer) return;

      const targetRect = target.getBoundingClientRect();
      const containerRect = scrollContainer.getBoundingClientRect();
      const targetTop =
        targetRect.top - containerRect.top + scrollContainer.scrollTop;
      const centeredTop =
        targetTop - (scrollContainer.clientHeight - target.offsetHeight) / 2;

      scrollContainer.scrollTo({
        top: Math.max(0, centeredTop),
        behavior: "smooth",
      });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [autoScrollToUpcoming, currentScrollKey, firstUpcomingIndex, loading]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
        <CircularProgress size={compact ? 24 : 32} />
      </Box>
    );
  }

  if (!items.length) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        textAlign="center"
        py={compact ? 2.5 : 6}
        px={2}
        color="#7B8494"
      >
        <EventBusyOutlinedIcon sx={{ fontSize: compact ? 30 : 42, mb: 1 }} />
        <Typography fontWeight={700} fontSize={compact ? 13 : 16}>
          {t("empty.title")}
        </Typography>
        {!compact && (
          <Typography variant="body2">{t("empty.description")}</Typography>
        )}
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap={compact ? 0.75 : 1}>
      {items.map((item, index) => {
        const impact = Number(item.impact);
        const style = IMPACT_STYLES[String(impact)] || IMPACT_STYLES["-1"];
        const eventIsPast = isCalendarEventPast(item.datetime, now);
        const key = `${item.datetime}-${item.currency}-${item.name}-${index}`;

        return (
          <Box
            key={key}
            ref={index === firstUpcomingIndex ? upcomingEventRef : null}
            sx={{
              display: "grid",
              gridTemplateColumns: compact
                ? "50px 68px minmax(0, 1fr) 32px"
                : {
                    xs: "52px 44px minmax(0, 1fr) 34px",
                    sm: "64px minmax(145px, 0.75fr) minmax(220px, 1.25fr) 36px",
                    md: "70px minmax(145px, 0.85fr) minmax(240px, 1.25fr) minmax(125px, 0.65fr) minmax(115px, 0.55fr) 38px",
                  },
              gap: compact ? 0.75 : { xs: 0.8, sm: 1.4 },
              alignItems: "center",
              p: compact ? 0.9 : { xs: 1.1, sm: 1.35 },
              borderRadius: compact ? "10px" : "14px",
              border: eventIsPast ? "1px dashed #D9DEE7" : "1px solid #E2E7EF",
              borderLeft: `4px solid ${eventIsPast ? "#B7C0CD" : style.dot}`,
              background: eventIsPast ? "#F6F8FA" : "#FFFFFF",
              opacity: eventIsPast ? 0.64 : 1,
              transition: "transform 160ms ease, box-shadow 160ms ease",
              "&:hover": {
                opacity: 1,
                transform: "translateY(-1px)",
                boxShadow: "0 8px 20px rgba(37, 62, 102, 0.08)",
              },
            }}
          >
            <Typography
              component="time"
              fontWeight={850}
              color={eventIsPast ? "#737D8C" : "#253E66"}
              fontSize={compact ? 12 : 14}
              sx={{ fontVariantNumeric: "tabular-nums" }}
            >
              {formatTime(item.datetime, i18n.language)}
            </Typography>

            {compact ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.55,
                  color: eventIsPast ? "#697382" : "#253E66",
                  fontSize: 10,
                  fontWeight: 900,
                  letterSpacing: 0.35,
                }}
              >
                <CountryFlag
                  country={item.country}
                  currency={item.currency}
                  isPast={eventIsPast}
                  compact
                />
                <Typography
                  component="span"
                  color="inherit"
                  fontSize={10}
                  fontWeight={900}
                >
                  {item.currency || "--"}
                </Typography>
              </Box>
            ) : (
              <Box display="flex" alignItems="center" gap={1} minWidth={0}>
                <CountryFlag
                  country={item.country}
                  currency={item.currency}
                  isPast={eventIsPast}
                />
                <Box minWidth={0} sx={{ display: { xs: "none", sm: "block" } }}>
                  <Typography
                    color={eventIsPast ? "#697382" : "#253E66"}
                    fontSize={12.5}
                    fontWeight={900}
                    letterSpacing={0.35}
                  >
                    {item.currency || "--"}
                  </Typography>
                  <Typography color="#7B8494" fontSize={10.5} noWrap>
                    {item.country || t("unknownCountry")}
                  </Typography>
                </Box>
              </Box>
            )}

            <Box minWidth={0}>
              <Box display="flex" alignItems="center" gap={0.7} minWidth={0}>
                {!compact && (
                  <Typography
                    component="span"
                    sx={{
                      display: { xs: "inline", sm: "none" },
                      color: "#253E66",
                      fontSize: 10,
                      fontWeight: 900,
                    }}
                  >
                    {item.currency}
                  </Typography>
                )}
                <Typography
                  fontWeight={750}
                  color={eventIsPast ? "#626C7A" : "#242B38"}
                  fontSize={compact ? 11.5 : 14.5}
                  noWrap={compact}
                >
                  {item.name}
                </Typography>
              </Box>
              {!compact && (
                <Typography
                  component="span"
                  sx={{
                    display: { xs: "inline", md: "none" },
                    color: eventIsPast ? "#667085" : "#1E756B",
                    fontSize: 9.5,
                    fontWeight: 850,
                  }}
                >
                  {t(`status.${eventIsPast ? "past" : "upcoming"}`)}
                </Typography>
              )}
            </Box>

            {!compact && (
              <Box
                sx={{
                  display: { xs: "none", md: "flex" },
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.7,
                  minHeight: 38,
                  px: 1.2,
                  borderRadius: "10px",
                  color: style.color,
                  background: style.background,
                  fontSize: 12,
                  fontWeight: 850,
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: style.dot,
                  }}
                />
                {t(`impact.${impact}`)}
              </Box>
            )}

            {!compact && (
              <Box
                sx={{
                  display: { xs: "none", md: "flex" },
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.65,
                  minHeight: 38,
                  px: 1,
                  borderRadius: "10px",
                  color: eventIsPast ? "#667085" : "#176B63",
                  background: eventIsPast ? "#ECEFF3" : "#E7F5F1",
                  fontSize: 11,
                  fontWeight: 850,
                }}
              >
                <Box
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    background: eventIsPast ? "#98A2B3" : "#2A9D8F",
                  }}
                />
                {t(`status.${eventIsPast ? "past" : "upcoming"}`)}
              </Box>
            )}

            <Tooltip
              title={t(
                eventNotifications.isSubscribed(item)
                  ? "eventNotifications.disable"
                  : "eventNotifications.enable"
              )}
            >
              <span>
                <IconButton
                  size="small"
                  disabled={
                    eventIsPast ||
                    eventNotifications.loading ||
                    !eventNotifications.available ||
                    eventNotifications.isUpdating(item)
                  }
                  onClick={(event) => {
                    event.stopPropagation();
                    eventNotifications.toggle(item);
                  }}
                  aria-label={t(
                    eventNotifications.isSubscribed(item)
                      ? "eventNotifications.disable"
                      : "eventNotifications.enable"
                  )}
                  sx={{
                    width: compact ? 30 : 34,
                    height: compact ? 30 : 34,
                    color: eventNotifications.isSubscribed(item)
                      ? "#D99000"
                      : "#718096",
                    background: eventNotifications.isSubscribed(item)
                      ? "#FFF2CC"
                      : "transparent",
                    "&:hover": {
                      color: "#B97800",
                      background: "#FFF2CC",
                    },
                  }}
                >
                  {eventNotifications.isUpdating(item) ? (
                    <CircularProgress size={compact ? 15 : 17} />
                  ) : eventNotifications.isSubscribed(item) ? (
                    <NotificationsActiveOutlinedIcon
                      sx={{ fontSize: compact ? 17 : 19 }}
                    />
                  ) : (
                    <NotificationsNoneOutlinedIcon
                      sx={{ fontSize: compact ? 17 : 19 }}
                    />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      })}
    </Box>
  );
};

EconomicCalendarEventList.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      datetime: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.instanceOf(Date),
      ]),
      currency: PropTypes.string,
      impact: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      country: PropTypes.string,
    })
  ),
  loading: PropTypes.bool,
  compact: PropTypes.bool,
  autoScrollToUpcoming: PropTypes.bool,
  autoScrollKey: PropTypes.string,
  notificationDate: PropTypes.string,
};

export default EconomicCalendarEventList;
